import React, { useEffect, useMemo, useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { whatsappApi, WhatsAppRecipient } from '../../../services/whatsappApi';
import { TIMEZONE_OPTIONS } from './phoneTimezone';

interface EditRecipientModalProps {
  open: boolean;
  clientId: string;
  recipient: WhatsAppRecipient | null;
  onClose: () => void;
  onSaved: () => void;
}

export const EditRecipientModal: React.FC<EditRecipientModalProps> = ({
  open,
  clientId,
  recipient,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [timezone, setTimezone] = useState('');
  const [optInStatus, setOptInStatus] = useState<'pending' | 'opted_in' | 'opted_out'>('opted_in');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (recipient) {
      setName(recipient.display_name || '');
      setTagsInput((recipient.tags || []).join(', '));
      setPreferredTime(recipient.preferred_time_local || '');
      setTimezone(recipient.timezone || '');
      setOptInStatus(recipient.opt_in_status || 'opted_in');
    }
  }, [recipient]);

  // Inject the recipient's current timezone into the dropdown options if it's
  // a less-common IANA zone not in our curated list — otherwise the <select>
  // would render an unmatched value and look empty.
  const tzOptions = useMemo(() => {
    if (!timezone) return TIMEZONE_OPTIONS;
    if (TIMEZONE_OPTIONS.some((o) => o.value === timezone)) return TIMEZONE_OPTIONS;
    return [...TIMEZONE_OPTIONS, { value: timezone, label: timezone, region: 'Other' }];
  }, [timezone]);

  const tzGroups = useMemo(() => {
    const groups: Record<string, typeof TIMEZONE_OPTIONS> = {};
    for (const o of tzOptions) {
      (groups[o.region] = groups[o.region] || []).push(o);
    }
    return groups;
  }, [tzOptions]);

  if (!open || !recipient) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const tags = tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      await whatsappApi.updateRecipient(clientId, recipient.id, {
        display_name: name.trim() || undefined,
        tags,
        preferred_time_local: preferredTime.trim() || undefined,
        timezone: timezone.trim() || undefined,
        opt_in_status: optInStatus,
      });
      toast.success('Recipient updated');
      onSaved();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to update recipient';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-medium text-gray-900">Edit Recipient</h3>
            <p className="text-xs text-gray-500 mt-0.5 font-sans">{recipient.phone_e164}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Display name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Tags (comma-separated)
            </label>
            <input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="cohort-a, diabetic, enrolled"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={optInStatus}
              onChange={(e) => setOptInStatus(e.target.value as 'pending' | 'opted_in' | 'opted_out')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <option value="opted_in">Opted in (will receive)</option>
              <option value="opted_out">Opted out (excluded)</option>
              <option value="pending">Pending</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Only opted-in recipients are enrolled by workflow triggers.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Preferred check-in time
              </label>
              <input
                type="time"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                {!timezone && <option value="">— select —</option>}
                {Object.entries(tzGroups).map(([region, opts]) => (
                  <optgroup key={region} label={region}>
                    {opts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRecipientModal;
